import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SearchResults, CountyResearchRecord } from "./types";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface CsvImportProps {
  onImportComplete: () => void;
}

interface ImportResult {
  successful: number;
  failed: number;
  total: number;
  failedRecords: { row: number; reason: string; data: any }[];
}

export function CsvImport({ onImportComplete }: CsvImportProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Map common column name variations to standard field names
  const normalizeColumnName = (header: string): string => {
    const normalized = header.toLowerCase().trim();
    
    // Map variations to standard field names
    const mappings: Record<string, string> = {
      // Seller/Grantor variations
      'seller': 'grantor_name',
      'grantor': 'grantor_name',
      'grantor name': 'grantor_name',
      'grantor (simplified)': 'grantor_name',
      'seller name': 'grantor_name',
      // Buyer/Grantee variations
      'buyer': 'grantee_name',
      'grantee': 'grantee_name',
      'grantee name': 'grantee_name',
      'buyer name': 'grantee_name',
      // Document number variations
      'doc #': 'document_number',
      'doc number': 'document_number',
      'document #': 'document_number',
      'document no': 'document_number',
      // Date variations
      'filing': 'filing_date',
      'date filed': 'filing_date',
      'file date': 'filing_date',
      'recorded date': 'filing_date',
      'recording date': 'filing_date',
      // Document type
      'doc type': 'document_type',
      'type': 'document_type',
      'document type': 'document_type',
      // Pages
      'pages': 'number_of_pages',
      'page count': 'number_of_pages',
      // Address variations
      'address': 'property_address',
      'prop address': 'property_address',
      'property address': 'property_address',
      'street address': 'property_address',
      'seller address': 'grantee_address',
      'buyer address': 'grantee_address',
      // Financial fields
      'amount': 'loan_amount',
      'loan': 'loan_amount',
      'rate': 'interest_rate',
      'payment': 'monthly_payment',
      'term': 'term_months',
      // URLs
      'pdf': 'pdf_url',
      'url': 'pdf_url',
      // Additional fields that can be stored in notes
      'municipality': 'municipality',
      'tax id prefix / area': 'tax_id',
    };
    
    return mappings[normalized] || header;
  };

  const parseDate = (dateStr: string): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    
    // Handle M/D/YYYY format (e.g., 6/13/2023)
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    
    // Handle YYYY-MM-DD format (already correct)
    if (dateStr.includes('-')) {
      return dateStr;
    }
    
    return new Date().toISOString().split('T')[0];
  };

  const detectCounty = (municipality: string): string => {
    if (!municipality) return 'wayne';
    
    const muni = municipality.toUpperCase();
    // Wayne County municipalities
    const wayneCities = [
      'DETROIT', 'DEARBORN', 'LINCOLN PARK', 'SOUTHGATE', 'TRENTON',
      'VAN BUREN TOWNSHIP', 'ROCKWOOD', 'GARDEN CITY', 'WOODHAVEN',
      'HAMTRAMCK', 'DEARBORN HEIGHTS', 'HURON TOWNSHIP', 'ROMULUS',
      'RIVER ROUGE', 'ECORSE', 'INKSTER', 'WYANDOTTE', 'GIBRALTAR',
      'LIVONIA', 'MELVINDALE', 'REDFORD TOWNSHIP', 'NORTHVILLE',
      'PLYMOUTH TOWNSHIP', 'WESTLAND', 'TAYLOR'
    ];
    
    if (wayneCities.some(city => muni.includes(city))) {
      return 'wayne';
    }
    
    return 'unknown';
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => normalizeColumnName(h.trim().replace(/^"|"$/g, '')));
    const rows: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const row: any = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || null;
      });
      
      rows.push(row);
    }

    return rows;
  };

  const analyzeOwnerFinance = (grantee: string, grantor: string) => {
    const bankKeywords = ['BANK', 'MORTGAGE', 'LENDING', 'FINANCE COMPANY', 'CREDIT UNION', 'TRUST COMPANY'];
    const individualIndicators = [/\b(AND|&)\b/, /\bTRUST\b/, /\bESTATE\b/];
    
    const granteeUpper = grantee?.toUpperCase() || '';
    const grantorUpper = grantor?.toUpperCase() || '';
    
    let score = 0;
    const reasons: string[] = [];
    
    const hasBank = bankKeywords.some(k => granteeUpper.includes(k) || grantorUpper.includes(k));
    if (!hasBank) {
      score += 40;
      reasons.push('No bank keywords detected');
    }
    
    if (individualIndicators.some(pattern => pattern.test(granteeUpper))) {
      score += 30;
      reasons.push('Individual/trust ownership pattern');
    }
    
    if (grantorUpper.includes('LLC') || grantorUpper.includes('INC')) {
      score += 15;
      reasons.push('Corporate grantor');
    }
    
    if (granteeUpper.split(/\s+/).length <= 4 && grantorUpper.split(/\s+/).length <= 4) {
      score += 15;
      reasons.push('Simple party names');
    }
    
    return { isOwnerFinance: score >= 50, score, reasons };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        toast({
          title: "Empty file",
          description: "The CSV file contains no data",
          variant: "destructive"
        });
        setIsProcessing(false);
        return;
      }

      setProgress({ current: 0, total: rows.length });
      const importedRecords: CountyResearchRecord[] = [];
      const failedRecords: { row: number; reason: string; data: any }[] = [];
      let ownerFinanceCount = 0;

      console.log('📊 Starting CSV import:', { totalRows: rows.length });
      console.log('📋 Sample row:', rows[0]);

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        
        // Validate required fields
        if (!row.document_number && !row.grantee_name && !row.grantor_name) {
          console.warn(`⚠️ Row ${i + 1}: Missing all critical fields, skipping`);
          failedRecords.push({ 
            row: i + 1, 
            reason: 'Missing document_number, grantee_name, and grantor_name',
            data: row
          });
          setProgress({ current: i + 1, total: rows.length });
          continue;
        }

        const analysis = analyzeOwnerFinance(row.grantee_name, row.grantor_name);
        
        // Build raw_data object with extra fields
        const rawData: any = {};
        if (row.municipality) rawData.municipality = row.municipality;
        if (row.tax_id) rawData.tax_id = row.tax_id;
        
        // Detect county from municipality if available
        const detectedCounty = row.municipality 
          ? detectCounty(row.municipality) 
          : (row.county || 'dickinson');
        
        const record = {
          county: detectedCounty,
          document_number: row.document_number || `CSV-${Date.now()}-${i}`,
          filing_date: parseDate(row.filing_date),
          document_type: row.document_type || 'Unknown',
          number_of_pages: row.number_of_pages ? parseInt(row.number_of_pages) : null,
          grantor_name: row.grantor_name || 'Unknown',
          grantee_name: row.grantee_name || 'Unknown',
          grantee_address: row.grantee_address,
          property_address: row.property_address,
          legal_description: row.legal_description,
          loan_amount: row.loan_amount ? parseFloat(row.loan_amount) : null,
          interest_rate: row.interest_rate ? parseFloat(row.interest_rate) : null,
          monthly_payment: row.monthly_payment ? parseFloat(row.monthly_payment) : null,
          term_months: row.term_months ? parseInt(row.term_months) : null,
          pdf_url: row.pdf_url,
          is_potential_owner_finance: analysis.isOwnerFinance,
          confidence_score: analysis.score,
          detection_reasons: analysis.reasons,
          status: row.grantee_address ? 'ready_for_melissa' : 'new',
          address_needs_review: !row.grantee_address,
          is_active: true,
          pdf_downloaded: false,
          pdf_parsed: false,
          ocr_attempted: false,
          raw_data: Object.keys(rawData).length > 0 ? rawData : null,
        };

        console.log(`📝 Row ${i + 1}: Attempting to insert`, { 
          docNum: record.document_number,
          county: record.county,
          grantor: record.grantor_name?.substring(0, 20),
          grantee: record.grantee_name?.substring(0, 20)
        });

        const { data, error } = await supabase
          .from('county_research')
          .insert(record)
          .select()
          .single();

        if (error) {
          console.error(`❌ Row ${i + 1} failed:`, error);
          failedRecords.push({ 
            row: i + 1, 
            reason: error.message,
            data: { docNum: record.document_number, grantor: record.grantor_name, grantee: record.grantee_name }
          });
        } else if (data) {
          console.log(`✅ Row ${i + 1} imported successfully`);
          importedRecords.push(data as CountyResearchRecord);
          if (analysis.isOwnerFinance) {
            ownerFinanceCount++;
            
            // Trigger address extraction if no address present
            if (!row.grantee_address && row.pdf_url) {
              supabase.functions.invoke('extract-seller-address', {
                body: { recordId: data.id }
              }).catch(err => console.error('Address extraction failed:', err));
            }
          }
        }

        setProgress({ current: i + 1, total: rows.length });
      }

      console.log('📊 Import Summary:', {
        total: rows.length,
        successful: importedRecords.length,
        failed: failedRecords.length,
        ownerFinance: ownerFinanceCount
      });

      if (failedRecords.length > 0) {
        console.error('❌ Failed records:', failedRecords);
      }

      // Auto-backup to Excel
      if (importedRecords.length > 0) {
        backupRecordsToExcel(importedRecords, 'csv_import', 'csv_import');
      }

      // Set results for display
      setImportResult({
        successful: importedRecords.length,
        failed: failedRecords.length,
        total: rows.length,
        failedRecords
      });

      onImportComplete();

      if (importedRecords.length === 0) {
        toast({
          title: "Import failed",
          description: `No records were imported. ${failedRecords.length} rows failed. See results below.`,
          variant: "destructive"
        });
      } else if (failedRecords.length > 0) {
        toast({
          title: "Partial import",
          description: `Imported ${importedRecords.length} of ${rows.length} records. ${failedRecords.length} failed.`
        });
      } else {
        toast({
          title: "Import complete",
          description: `Imported ${importedRecords.length} records. Found ${ownerFinanceCount} potential owner-financed deals.`
        });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Error processing CSV:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to process CSV file",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Card className={importResult.failed > 0 ? "border-destructive" : "border-green-600"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult.failed > 0 ? (
                <AlertCircle className="h-5 w-5 text-destructive" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{importResult.successful}</div>
                <div className="text-sm text-muted-foreground">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-destructive">{importResult.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{importResult.total}</div>
                <div className="text-sm text-muted-foreground">Total Rows</div>
              </div>
            </div>

            {importResult.failedRecords.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Failed Records:</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {importResult.failedRecords.map((fail, idx) => (
                    <div key={idx} className="bg-destructive/10 p-3 rounded-lg text-sm">
                      <div className="font-medium">Row {fail.row}</div>
                      <div className="text-destructive">{fail.reason}</div>
                      {fail.data && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {JSON.stringify(fail.data, null, 2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setImportResult(null)}
              className="w-full"
            >
              Clear Results
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Import County Records from CSV</CardTitle>
          <CardDescription>
            Upload a CSV file exported from Tyler Host or manually created with county record data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Required CSV Columns
            </h4>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li><strong>document_number</strong> - Unique document identifier</li>
              <li><strong>filing_date</strong> - Date filed (YYYY-MM-DD)</li>
              <li><strong>grantee_name</strong> - Buyer/Grantee name</li>
              <li><strong>grantor_name</strong> - Seller/Grantor name</li>
              <li><em>Optional:</em> property_address, grantee_address, pdf_url, loan_amount, etc.</li>
            </ul>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            disabled={isProcessing}
          />

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="w-full"
            size="lg"
          >
            <Upload className="mr-2 h-4 w-4" />
            {isProcessing ? "Processing..." : "Upload CSV File"}
          </Button>

          {isProcessing && progress.total > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing records...</span>
                    <span>{progress.current} / {progress.total}</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">What Happens After Import?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Owner-Finance Detection</p>
              <p className="text-sm text-muted-foreground">
                Analyzes grantee/grantor names to identify potential owner-financed deals
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Automatic Address Extraction</p>
              <p className="text-sm text-muted-foreground">
                For records with pdf_url but no grantee_address, Claude Vision extracts seller addresses from PDFs
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Manual Review</p>
              <p className="text-sm text-muted-foreground">
                Records flagged for review can be processed individually in the Active Results tab
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
